/**
 * Панель самостоятельного задания (E4.6c, макет training-exercise): цели по порядку,
 * подсказка к текущей, сворачивание и «Продолжить позже». Панель только показывает ход
 * задания -- форма страницы под ней остаётся как есть. Задание идёт в учебном примере:
 * в рабочем контуре панели нет, а задание считается отложенным (exerciseResumable).
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { WorkspaceIcon } from '@/components/workspace/WorkspaceIcon';
import { useWorkspaceDataSource } from '@/features/workspace/WorkspaceDataSourceContext';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { EXERCISES } from './catalog';
import { exerciseTextParams } from './exercises';
import {
	pauseExercise,
	toggleExerciseCollapsed,
	toggleExerciseHint,
	useExerciseStore,
} from './exerciseStore';
import { hintKey } from './roleHints';
import { LIVE_MODE } from './types';
import { useTrainingProgress } from './useTrainingProgress';

export function ExercisePanel() {
	const { t } = useTranslation('learning');
	const { active, collapsed, hint, feedback } = useExerciseStore();
	const { mode } = useWorkspaceDataSource();
	const { progress, warning } = useTrainingProgress();
	const isAdmin = useIsAdmin();
	if (active === null || mode === LIVE_MODE) return null;

	const exercise = EXERCISES[active];
	const done = progress.exercises[active]?.done ?? [];
	const complete = done.length === exercise.goals.length;
	const goal = exercise.goals[done.length];
	const status = complete
		? warning === null
			? t('exercise.doneSaved')
			: t('exercise.doneNotSaved')
		: feedback === 'verification_failed'
			? t('exercise.verificationFailed')
			: feedback === 'not_done'
				? t('exercise.notDone')
				: t('exercise.pending');

	return (
		<section
			className={complete ? 'ws-training-exercise ws-is-complete' : 'ws-training-exercise'}
			aria-label={t(`learning:exercises.${active}.title`)}
		>
			<div className="ws-exercise-header">
				<span className="ws-exercise-symbol">
					<WorkspaceIcon name={complete ? 'check' : exercise.icon} size={20} />
				</span>
				<div>
					<span className="ws-training-kicker">{t('exercise.kicker')}</span>
					<h2 tabIndex={-1}>{t(`learning:exercises.${active}.title`)}</h2>
				</div>
				<span className="ws-exercise-progress">{`${done.length} / ${exercise.goals.length}`}</span>
				<button
					type="button"
					className="ws-icon-button"
					aria-label={collapsed ? t('exercise.expand') : t('exercise.collapse')}
					aria-expanded={!collapsed}
					onClick={toggleExerciseCollapsed}
				>
					<WorkspaceIcon name={collapsed ? 'down' : 'list'} size={16} />
				</button>
			</div>
			{!collapsed && (
				<div className="ws-exercise-body">
					<p>{t(`learning:exercises.${active}.description`, exerciseTextParams())}</p>
					<ol className="ws-exercise-checklist">
						{exercise.goals.map((id, index) => (
							<li
								key={id}
								className={
									index < done.length ? 'ws-done' : index === done.length ? 'ws-current' : undefined
								}
							>
								<span>
									{index < done.length ? <WorkspaceIcon name="check" size={14} /> : index + 1}
								</span>
								{t(`learning:exercises.${active}.goals.${id}.title`)}
							</li>
						))}
					</ol>
					{hint && goal && (
						<div className="ws-exercise-hint">
							<WorkspaceIcon name="report" size={16} />
							<p>
								{t(hintKey(`exercises.${active}.goals.${goal}`, isAdmin), exerciseTextParams())}
							</p>
						</div>
					)}
					<div className="ws-exercise-footer">
						<p role="status">{status}</p>
						<div>
							{complete ? (
								<>
									<Link className="ws-btn" to="/learning" onClick={() => pauseExercise(true)}>
										{t('exercise.all')}
									</Link>
									<button
										type="button"
										className="ws-btn ws-primary"
										onClick={() => pauseExercise(true)}
									>
										{t('exercise.finish')}
									</button>
								</>
							) : (
								<>
									<button
										type="button"
										className="ws-btn"
										aria-expanded={hint}
										onClick={toggleExerciseHint}
									>
										{hint ? t('exercise.hideHint') : t('exercise.hint')}
									</button>
									<button
										type="button"
										className="ws-text-link"
										onClick={() => pauseExercise(false)}
									>
										{t('exercise.later')}
									</button>
								</>
							)}
						</div>
					</div>
					<small className="ws-exercise-note">{t('exercise.note')}</small>
				</div>
			)}
		</section>
	);
}
